/**
 * Shared color-by legend model for the maneuver pages (MAP / SCATTER / TIME SERIES / TABLE).
 *
 * Consolidates the four near-identical getLegendGroups() memos that live in
 * components/maneuvers/{standard,grouped}/{Scatter,TimeSeries}.tsx so every view of a page reads
 * one legend built from one row source.
 *
 * Two-pass on purpose: buildManeuverColorLegendItems is the expensive grouping/coloring pass
 * (re-runs on data or color-mode change), applyManeuverLegendSelection is the cheap selection pass
 * (re-runs on every pill click). Keep them separate so a click never re-runs buildColorGrouping
 * over thousands of rows.
 *
 * No color code of its own: every branch delegates to colorGrouping.ts / maneuverScatterTimeseries.ts
 * so the pills cannot drift from the plotted points.
 */
import { groupBy } from './global';
import {
  buildColorGrouping,
  collectManeuverIdsForLegendItem,
  groupKeyEquals,
  legendTextToGroupKeyTable,
} from './colorGrouping';
import { maneuverTimelineLegendGroups } from './maneuverScatterTimeseries';

export interface ManeuverColorLegendItem {
  /** Raw group value — this is what goes to onItemClick, i.e. the page's handleLegendClick. */
  label: string;
  /** Display text: '0'/'' → 'NONE'; otherwise identical to label. */
  displayLabel: string;
  color: string;
  isHighlight?: boolean;
  markerShape?: 'circle' | 'square';
  isBaseline?: boolean;
  /** event_ids in this group, for the cheap selection pass (empty for the baseline entry). */
  eventIds: number[];
}

type Row = Record<string, unknown>;

/** The color modes whose groups come straight from buildColorGrouping. */
const CATEGORICAL_MODES = new Set(['SOURCE', 'RACE', 'STATE', 'CONFIG', 'CREW', 'TESTS', 'YEAR', 'EVENT']);

/**
 * Legend TWS bins are the fixed set, not the bins present in the data — preserving the behaviour of
 * the d3 legend this replaced, which excluded 10 deliberately.
 */
const FIXED_TWS_LEGEND_BINS = [15, 20, 25, 30, 35, 40, 45, 50] as const;

function toDisplayLabel(label: string): string {
  return label === '0' || label === '' ? 'NONE' : label;
}

export interface BuildManeuverColorLegendOptions {
  /** Current maneuver event type — TACK labels are event-type dependent (see below). */
  eventType?: string | null;
  /**
   * Rows the VMG intervals are computed from. calculateVmgIntervals uses mean±1σ over whatever it
   * is given, so this MUST match the row set the page passes when it resolves a VMG legend click,
   * or every VMG pill selects nothing.
   */
  vmgIntervalSource?: readonly Row[];
  /** Colour comes from maneuverBaselineStore so the pill matches the baseline drawn on the plots. */
  baseline?: { name: string; color?: string } | null;
}

/**
 * Rows → legend items with colors and per-group eventIds. No selection state — see
 * applyManeuverLegendSelection. `rows` should already be the page's filtered/visible set.
 */
export function buildManeuverColorLegendItems(
  rows: readonly Row[],
  colorMode: string | undefined,
  opts?: BuildManeuverColorLegendOptions
): ManeuverColorLegendItem[] {
  const mode = String(colorMode || 'TWS').trim().toUpperCase();
  const list = Array.isArray(rows) ? (rows as Row[]) : [];

  const baselineName = opts?.baseline?.name?.trim();
  const baselineEntry: ManeuverColorLegendItem[] = opts?.baseline
    ? [{
        label: baselineName || 'Baseline',
        displayLabel: baselineName || 'Baseline',
        color: opts.baseline.color || '#fff',
        markerShape: 'circle',
        isBaseline: true,
        eventIds: [],
      }]
    : [];

  if (list.length === 0) {return baselineEntry;}

  const idsFor = (label: string): number[] =>
    collectManeuverIdsForLegendItem(list, mode, label, {
      vmgIntervalSource: opts?.vmgIntervalSource ?? list,
    });

  let items: ManeuverColorLegendItem[] = [];

  if (mode === 'TWS') {
    // Colour each fixed bin through the same scale the points use, by probing with the field
    // extractValue() reads for TWS (tws_bin).
    const { getItemColor } = buildColorGrouping(list, 'TWS');
    items = FIXED_TWS_LEGEND_BINS.map((bin) => {
      const label = String(bin);
      return {
        label,
        displayLabel: label,
        color: String(getItemColor({ tws_bin: bin } as Row)),
        eventIds: idsFor(label),
      };
    });
  } else if (mode === 'VMG') {
    // maneuverTimelineLegendGroups already builds the 5 mean±1σ intervals and colours them; the
    // interval math must come from the same rows the click resolver uses.
    const source = (opts?.vmgIntervalSource ?? list) as Row[];
    items = maneuverTimelineLegendGroups(source, 'VMG').map((group) => ({
      label: group.name,
      displayLabel: group.name,
      color: group.color,
      eventIds: idsFor(group.name),
    }));
  } else if (mode === 'TACK') {
    // Label derivation copied from components/maneuvers/standard/Scatter.tsx so the pills read
    // exactly as the d3 legend did: single-tack maneuver types show PORT/STBD, tacks and gybes
    // show the raw 'P - S' / 'S - P' value.
    //
    // Under those types both 'STBD' and 'S - P' collapse to 'STBD', so dedupe by display label or
    // the same pill is emitted twice.
    const evt = String(opts?.eventType || '').toUpperCase();
    const usePortStbdLabels = evt === 'TAKEOFF' || evt === 'BEARAWAY' || evt === 'ROUNDUP';
    const byLabel = new Map<string, ManeuverColorLegendItem>();
    for (const raw of groupBy(list as unknown[], 'tack') as unknown[]) {
      const tack = String(raw);
      if (tack.trim() === '' || tack.toLowerCase() === 'unknown') {continue;}
      const isStbd = tack === 'STBD' || tack === 'S - P';
      const label = usePortStbdLabels ? (isStbd ? 'STBD' : 'PORT') : tack;
      if (byLabel.has(label)) {continue;}
      const color = isStbd ? '#2ca02c' : '#d62728';
      byLabel.set(label, {
        label,
        displayLabel: toDisplayLabel(label),
        // Port family square, starboard circle — the d3 legend's marker rule.
        markerShape: color === '#d62728' ? 'square' : 'circle',
        color,
        eventIds: idsFor(label),
      });
    }
    items = [...byLabel.values()];
  } else if (CATEGORICAL_MODES.has(mode)) {
    items = maneuverTimelineLegendGroups(list, mode)
      .filter((group) => {
        const name = String(group.name ?? '').trim().toLowerCase();
        return name !== '' && name !== 'unknown';
      })
      .map((group) => ({
        label: group.name,
        displayLabel: toDisplayLabel(group.name),
        color: group.color,
        eventIds: idsFor(group.name),
      }));
    // STATE keeps buildColorGrouping's canonical reading order; the rest sort alphabetically, as
    // the d3 legend did.
    if (mode !== 'STATE') {
      items.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
    }
  }

  return [...items, ...baselineEntry];
}

export interface ManeuverLegendSelection {
  grouped: boolean;
  selectedGroupKeys?: readonly (string | number)[];
  selectedEventIds?: readonly number[];
}

/**
 * Cheap second pass: marks isHighlight from the current selection. Grouped mode compares group
 * keys the way the grouped DataTable does; non-grouped mode lights a pill exactly when every event
 * in that group is selected.
 */
export function applyManeuverLegendSelection(
  items: readonly ManeuverColorLegendItem[],
  colorMode: string | undefined,
  sel: ManeuverLegendSelection
): ManeuverColorLegendItem[] {
  const mode = String(colorMode || 'TWS').trim().toUpperCase();

  if (sel.grouped) {
    const keys = sel.selectedGroupKeys ?? [];
    if (keys.length === 0) {return items.map((it) => ({ ...it, isHighlight: false }));}
    return items.map((it) => ({
      ...it,
      isHighlight:
        !it.isBaseline &&
        keys.some((k) => groupKeyEquals(k, legendTextToGroupKeyTable(it.label, mode))),
    }));
  }

  const ids = sel.selectedEventIds ?? [];
  if (ids.length === 0) {return items.map((it) => ({ ...it, isHighlight: false }));}
  const idSet = new Set(ids);
  return items.map((it) => ({
    ...it,
    isHighlight:
      !it.isBaseline && it.eventIds.length > 0 && it.eventIds.every((id) => idSet.has(id)),
  }));
}
